import { Trans } from '@lingui/macro';
import { useReducer, useState } from "react";
import { DocuBot } from "../../../models/DocuBot";
import { Room } from "../../../models/Room";
import { Fire } from "../../../services/FireClient";
import LimitedFeature from "../../components/LimitedFeature";
import ProfileImg from "../../components/ProfileImg";
import { DocumentBotEditModal } from "./BotDocumentEditModal";

import { Button } from "../../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../../../components/ui/avatar";
import { Badge } from "../../../components/ui/badge";

export const BotDocumentPart: React.FC<{ room: Room }> = ({ room }) => {
  const [selectedDocuBot, setSelectedDocuBot] = useState<DocuBot>();
  const docuBots: DocuBot[] = room.bots?.filter(b => b.type === 'ai-docubot').map(b => b as DocuBot) ?? [];
  const [_, forceUpdate] = useReducer((x) => x + 1, 0);

  return (
    <Card className="w-full mt-4 border-dashed border-destructive/20 bg-destructive/10">
      <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
        <div className="flex items-center gap-2">
          <CardTitle className="text-lg font-medium text-[color:rgb(var(--text-title))]">
            <Trans>Document bots</Trans>
          </CardTitle>
          <Badge variant="destructive" className="text-meta-sm h-5">
            <Trans>DEPRECATED</Trans>
          </Badge>
        </div>
        <LimitedFeature
          workspaceId={room.workspaceId}
          tier={'PREMIUM'}
          content={() => (
            <Button size="sm" variant="secondary" onClick={() => addBot()} disabled={true}>
              <Trans>Add</Trans>
            </Button>
          )}
        />
      </CardHeader>

      <CardContent className="grid gap-2">
        {docuBots.length === 0 && (
          <p className="text-sm text-muted-foreground italic py-2">
            <Trans>Click add to train a bot with documents.</Trans>
          </p>
        )}

        {docuBots.map(b => getBotItem(b))}

        {selectedDocuBot && (
          <DocumentBotEditModal
            room={room}
            bot={selectedDocuBot}
            onDone={handleDone}
            onRemove={handleRemove}
          />
        )}
      </CardContent>
    </Card>
  );

  function handleDone() {
    if (selectedDocuBot?.isProcessing)
      setSelectedDocuBot(undefined);
    else
      saveSelectedDocuBotAndClear();
  }

  function handleRemove() {
    if (selectedDocuBot && room.bots?.find(b => selectedDocuBot.id)) {
      Fire.deleteDocuBot(selectedDocuBot);
      room.bots = room.bots.filter(b => b.id != selectedDocuBot.id);
      saveSelectedDocuBotAndClear();
    }
  }

  function saveSelectedDocuBotAndClear() {
    if (room.bots) {
      Fire.updateRoomBots(room.id, room.bots);
    }
    setSelectedDocuBot(undefined);
    forceUpdate();
  }

  function addBot(): void {
    const newBot = new DocuBot(room.id);
    const nrOfDocuBots = room.bots?.filter(b => b.type === 'ai-docubot').length;
    if (nrOfDocuBots && nrOfDocuBots > 0) {
      newBot.name += ` ${nrOfDocuBots}`;
    }
    room.bots = room.bots ?? [];
    room.bots.push(newBot);
    Fire.updateRoomBots(room.id, room.bots);
    setSelectedDocuBot(newBot);
  }

  function getBotItem(b: DocuBot) {
    return (
      <div
        key={b.id}
        onClick={() => editBot(b)}
        className="flex items-center space-x-3 p-2 rounded-lg hover:bg-background hover:shadow-sm cursor-pointer transition-all border border-transparent hover:border-border"
      >
        <Avatar className="h-10 w-10">
          <AvatarImage src={`/assets/img/${b.type}.png`} alt={b.name} />
          <AvatarFallback>{b.name[0]}</AvatarFallback>
          <div className="absolute inset-0 flex items-center justify-center w-full h-full bg-transparent">
            <ProfileImg url={`/assets/img/${b.type}.png`} className="w-full h-full object-cover rounded-full" />
          </div>
        </Avatar>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm text-[color:rgb(var(--text-body))] truncate">{b.name}</span>
            {!b.isIndexCreated && (
              <span className="text-xs text-destructive bg-destructive/10 px-1 rounded">
                <Trans>Not ready</Trans>
              </span>
            )}
            {b.isProcessing && (
              <span className="text-xs text-muted-foreground bg-muted px-1 rounded">
                <Trans>Processing</Trans>
              </span>
            )}
          </div>

          <div className="text-xs text-muted-foreground">
            <Trans>{(b.files?.length ?? 0)} documents.</Trans>
          </div>

          {b.afterPrompt && (
            <div className="text-xs text-muted-foreground truncate">
              <Trans>After the prompt: {b.afterPrompt}</Trans>
            </div>
          )}
        </div>
      </div>
    );
  }

  function editBot(b: DocuBot) {
    setSelectedDocuBot(b);
  }
}
export default BotDocumentPart;